// Styles
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useBQMQ } from "context/BQMQ.context";
import { useEffect, useState } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import LoadingSpinner from "../../../Common/LoadingSpinner/LoadingSpinner";
import HomeListContainer from "./HomeList.styles";

type Props = {
    data: any[];
    row: any;
    title: any;
    count?: number;
    countColor: string;
    seeAll?: Function;
    maxRows?: number | false;
    loading?: boolean;
    sortable?: boolean;
    onReorder?: (newOrder: any[]) => void;
};

function SortableRow({ item, index, row }: { item: any; index: number; row: any }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.tableID || item.id || index });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={`listRow ${isDragging ? 'dragging' : ''}`}
        >
            {row(item, index)}
        </div>
    );
}

export default function HomeList({
    data,
    row,
    title,
    count,
    countColor,
    seeAll,
    maxRows,
    loading,
    sortable = false,
    onReorder,
}: Props) {
    const { bqmqLoading } = useBQMQ();
    const [items, setItems] = useState(data);

    // Update items when data changes
    useEffect(() => {
        setItems(data);
    }, [data]);

    if (!loading) {
        loading = bqmqLoading;
    }
    useEffect(() => {
        loading = bqmqLoading;
    }, [bqmqLoading]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setItems((items) => {
                const oldIndex = items.findIndex(
                    (item) => (item.tableID || item.id || items.indexOf(item)) === active.id
                );
                const newIndex = items.findIndex(
                    (item) => (item.tableID || item.id || items.indexOf(item)) === over?.id
                );

                const newOrder = arrayMove(items, oldIndex, newIndex);
                
                if (onReorder) {
                    onReorder(newOrder);
                }

                return newOrder;
            });
        }
    };

    const displayItems = maxRows ? items.slice(0, maxRows) : items;

    return (
        <HomeListContainer>
            <div className={"titleRow"}>
                <div className={"titleText"}>{title}</div>
                {
                    <div className={"count " + countColor}>
                        {loading && !count ? <LoadingSpinner noText /> : count}
                    </div>
                }
            </div>

            <div className={"listBody"}>
                {sortable ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={displayItems.map((item, index) => item.tableID || item.id || index)}
                            strategy={verticalListSortingStrategy}
                        >
                            {displayItems.map((item, index) => (
                                <SortableRow
                                    key={`${item.tableID || item.id}${index}`}
                                    item={item}
                                    index={index}
                                    row={row}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                ) : (
                    displayItems.map((item, index) => (
                        <div
                            className={"listRow"}
                            key={`${item.tableID || item.id}${index}`}
                        >
                            {row(item, index)}
                        </div>
                    ))
                )}
            </div>

            {maxRows && seeAll && items.length > maxRows && (
                <div className={"seeAll"} onClick={() => seeAll()}>
                    SEE ALL <FontAwesomeIcon icon={faArrowRight} fixedWidth />
                </div>
            )}
        </HomeListContainer>
    );
}
